import * as React from 'react'
import { Link } from 'react-router-dom'
import {
  CheckCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import './styles.css'
import Image2 from '../../../../assets/imgs/museums/image2.jpg'
import Image3 from '../../../../assets/imgs/museums/image3.jpg'
import Image4 from '../../../../assets/imgs/museums/image4.jpg'
import VideoPreviewImage from '../../../../assets/imgs/museums/video-preview.jpg'

const DELIVERABLES = [
  'Structured CMS architecture designed for long-term sustainability',
  'API-led integration with Collections Management Systems and DAMS',
  'Accessible-by-default design aligned to WCAG 2.2 AA',
  'Sustainable content modelling and taxonomy design',
  'High-performance search and discovery tools',
  'Secure UK-hosted cloud infrastructure',
  'Clear governance and editorial workflow configuration',
]

const CulturalCollectionsDigital = () => {
  const [isHeroVideoReady, setIsHeroVideoReady] = React.useState(false)
  const [hasHeroVideoError, setHasHeroVideoError] = React.useState(false)
  const heroVideoRef = React.useRef(null)

  React.useEffect(() => {
    if (hasHeroVideoError || !heroVideoRef.current) return

    const playPromise = heroVideoRef.current.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {})
    }
  }, [hasHeroVideoError])

  return (
    <div className='eu-ccd-page'>
    <section className='overview-section eu-ccd-hero'>
      <div className='eu-ccd-hero-bg' />
      <div className='box'>
        <h1 className='eu-ccd-hero-title eu-ccd-hero-heading'>
          Digital Infrastructure for Museums, Galleries & Public Collections
        </h1>
        <div className='eu-ccd-hero-grid'>
          <div className='eu-ccd-hero-content'>
            <p className='eu-ccd-hero-subtitle'>
              Pod Europe designs structured, accessible, and integration-led
              digital platforms for cultural institutions managing complex
              collections and diverse public audiences.
            </p>
            <Link to='/contact' className='eu-ccd-primary-cta'>
              Start a conversation
            </Link>
          </div>
            <div className='eu-ccd-hero-media'>
              {!isHeroVideoReady && !hasHeroVideoError && (
                <img
                  src={VideoPreviewImage}
                  alt='Museum interior preview while video is loading'
                />
              )}
              {hasHeroVideoError ? (
                <img
                  src={VideoPreviewImage}
                  alt='Museum interior preview while video is unavailable'
                />
              ) : (
                <video
                  ref={heroVideoRef}
                  src='https://atlasjobs.blob.core.windows.net/samples-workitems/MuseumVideo.mp4'
                  autoPlay
                  loop
                  muted
                  defaultMuted
                  playsInline
                  preload='metadata'
                  poster={VideoPreviewImage}
                  onLoadedMetadata={() => setIsHeroVideoReady(true)}
                  onLoadedData={() => setIsHeroVideoReady(true)}
                  onCanPlay={() => setIsHeroVideoReady(true)}
                  onError={() => setHasHeroVideoError(true)}
                />
              )}
            </div>
        </div>
      </div>
    </section>

    <section className='overview-section eu-ccd-section'>
      <div className='box'>
        <h2 className='eu-ccd-section-title eu-ccd-section-heading'>
          The Challenge
        </h2>
        <div className='eu-ccd-section-grid'>
          <div className='eu-ccd-copy'>
            <p>
              Cultural institutions today manage increasingly complex digital
              ecosystems: collections databases, digital asset management
              systems, CRM platforms, ticketing systems, and evolving audience
              expectations.
            </p>
            <p>
              Public websites must do more than present information - they must
              connect structured metadata, high-quality digital assets,
              interpretation, and institutional content in a seamless and
              accessible experience.
            </p>
            <p>
              We work with institutions to design digital platforms that are
              technically robust, editorially sustainable, and aligned to
              long-term strategic goals.
            </p>
          </div>
          <div className='eu-ccd-section-media'>
            <img
              src={Image2}
              alt='Museum interior illustrating complex digital content challenges'
            />
            <div className='eu-ccd-hero-media-chip'>
              <ShieldCheckIcon />
              <span>Built for discoverability</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className='overview-section eu-ccd-section eu-ccd-section-soft'>
      <div className='box'>
        <h2 className='eu-ccd-section-title eu-ccd-section-heading'>
          What We Deliver
        </h2>
        <div className='eu-ccd-copy eu-ccd-copy-wide'>
          <div className='eu-ccd-deliverables'>
            {DELIVERABLES.map((item) => (
              <div key={item} className='eu-ccd-deliverable'>
                <CheckCircleIcon className='eu-ccd-deliverable-icon' />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <p>
            Our focus is not simply on building websites, but on creating
            durable digital infrastructure that can evolve with institutional
            needs.
          </p>
        </div>
      </div>
    </section>

    <section className='overview-section eu-ccd-section'>
      <div className='box'>
        <h2 className='eu-ccd-section-title eu-ccd-section-heading'>
          Our Design & Delivery Approach
        </h2>
        <h3 className='eu-ccd-subtitle eu-ccd-subtitle-heading'>
          Live Design Studio Sessions
        </h3>
        <div className='eu-ccd-section-grid'>
          <div className='eu-ccd-copy'>
            <p>
              We deliver design through facilitated Live Design Studio sessions.
              Rather than presenting static mock-ups weeks after initial
              discussions, we collaborate directly with stakeholders in real
              time, evolving layouts, navigation structures, and content
              hierarchies live within the session.
            </p>
            <p>
              This approach enables immediate testing of ideas, rapid iteration
              of interaction patterns, and transparent design decision-making.
              It ensures that the final platform reflects institutional
              priorities while remaining user-centred and accessible.
            </p>
            <p>
              All sessions are conducted in dual-view format, designing mobile
              and desktop experiences concurrently to ensure responsive
              behaviour is considered from the outset rather than retrofitted
              later.
            </p>
          </div>
          <div className='eu-ccd-section-media'>
            <img
              src={Image3}
              alt='Collaborative design planning session in a gallery context'
            />
            <div className='eu-ccd-hero-media-chip'>
              <ShieldCheckIcon />
              <span>Co-designed with institutional teams</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className='overview-section eu-ccd-section'>
      <div className='box'>
        <h2 className='eu-ccd-section-title eu-ccd-section-heading'>
          Structured Collections Publishing
        </h2>
        <div className='eu-ccd-section-grid eu-ccd-section-grid-reverse'>
          <div className='eu-ccd-section-media'>
            <img
              src={Image4}
              alt='Structured collections display representing publishing workflows'
            />
            <div className='eu-ccd-hero-media-chip'>
              <ShieldCheckIcon />
              <span>Reliable publishing across systems</span>
            </div>
          </div>
          <div className='eu-ccd-copy'>
            <p>
              Our expertise in structured data architecture allows us to
              implement reliable, API-driven integrations between collections
              management systems, digital asset platforms, and public-facing
              websites.
            </p>
            <p>
              We design automated synchronisation processes that ensure metadata
              accuracy, media consistency, and sustainable editorial workflows -
              reducing manual duplication and long-term maintenance burden.
            </p>
            <p>
              Search and discovery tools are built around structured taxonomies
              and filtering logic, enabling audiences to explore collections
              intuitively across devices.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className='overview-section eu-ccd-section eu-ccd-section-soft'>
      <div className='box'>
        <h2 className='eu-ccd-section-title eu-ccd-section-heading'>
          Secure & Sustainable by Design
        </h2>
        <div className='eu-ccd-copy eu-ccd-copy-wide'>
          <p>
            Pod Europe delivers platforms aligned to UK public-sector standards,
            with secure cloud hosting, role-based access controls, encrypted
            data transfer, and structured monitoring processes.
          </p>
          <p>
            Our architectures are modular and future-ready, minimising technical
            debt and supporting long-term evolution as institutional needs grow.
          </p>
          <p>
            We prioritise clarity, maintainability, and sustainability over
            short-term technical trends.
          </p>
        </div>
      </div>
    </section>

    <section className='overview-section eu-ccd-cta'>
      <div className='box'>
        <div className='eu-ccd-cta-panel'>
          <p>
            If you are planning a digital transformation or collections
            publishing initiative, we would welcome a conversation.
          </p>
          <Link to='/contact' className='eu-ccd-secondary-cta'>
            Start a Conversation
          </Link>
        </div>
      </div>
    </section>
    </div>
  )
}

export default CulturalCollectionsDigital
